/**
 * CPR Home Page
 *
 * Main landing page for CPR Training Direct featuring:
 * - Hero section with primary CTAs
 * - Regional service badges
 * - Services overview
 * - Accreditation logos
 * - Testimonials carousel
 * - Bottom CTA banner
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { HeroSlideshow } from '../../components/cpr/HeroSlideshow';
import { ServicesOverview } from '../../components/cpr/ServicesOverview';
import { TestimonialsCarousel } from '../../components/cpr/TestimonialsCarousel';
import { CTABanner } from '../../components/cpr/CTABanner';
import { AccreditationLogos } from '../../components/cpr/AccreditationLogos';
import { getFeaturedTestimonials } from '../../data/testimonials';

const CPRHome: React.FC = () => {
  // Featured testimonials only (featured: true)
  const featuredTestimonials = getFeaturedTestimonials();

  // Services data for ServicesOverview component
  const services = [
    {
      icon: '❤️',
      title: 'CPR & AED Certification',
      description:
        'Initial certification courses for individuals and healthcare providers. Learn life-saving techniques with hands-on training and receive same-day certification.',
      ctaText: 'Learn More',
      ctaLink: '/courses/cpr-aed',
    },
    {
      icon: '🔄',
      title: 'Fast Renewal',
      description:
        'Quick recertification courses for those maintaining their credentials. Blended learning options available with online theory and in-person skills assessment.',
      ctaText: 'Renew Now',
      ctaLink: '/courses/cpr-renewal',
    },
    {
      icon: '👥',
      title: 'Group Training',
      description:
        'On-site training for businesses, schools, and organizations. Convenient mobile training brings certification to your location with flexible scheduling.',
      ctaText: 'Get Quote',
      ctaLink: '/courses/group-training',
    },
  ];

  // Accreditation logos - American Heart Association
  const accreditationLogos = [
    {
      name: 'American Heart Association',
      src: '/AHA_LOGO-HEX_Red+Black_LG.jpg',
      alt: 'American Heart Association (AHA) Certified Training Center',
    },
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>CPR Training Direct | Professional CPR Certification in Orlando & Atlanta</title>
        <meta name="title" content="CPR Training Direct | Professional CPR Certification in Orlando & Atlanta" />
        <meta
          name="description"
          content="Get CPR certified in Orlando or Atlanta with same-day certification cards. AHA certified courses, mobile training available, and fast renewal options. Book your class today!"
        />
        <meta
          name="keywords"
          content="CPR training, CPR certification, AED training, Orlando CPR, Atlanta CPR, BLS certification, American Heart Association, same day certification"
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/" />
        <meta property="og:title" content="CPR Training Direct | Professional CPR Certification in Orlando & Atlanta" />
        <meta
          property="og:description"
          content="Get CPR certified in Orlando or Atlanta with same-day certification cards. AHA certified courses, mobile training available, and fast renewal options. Book your class today!"
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/" />
        <meta name="twitter:title" content="CPR Training Direct | Professional CPR Certification in Orlando & Atlanta" />
        <meta
          name="twitter:description"
          content="Get CPR certified in Orlando or Atlanta with same-day certification cards. AHA certified courses, mobile training available, and fast renewal options. Book your class today!"
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />

        {/* Schema.org Structured Data - Organization + LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://cprtrainingdirect.com/#organization",
                "name": "CPR Training Direct",
                "url": "https://cprtrainingdirect.com",
                "logo": "https://cprtrainingdirect.com/CRPTrainingDirectLogo.png",
                "description": "Professional CPR certification and training in Orlando, FL and Atlanta, GA. AHA certified courses with same-day certification.",
                "contactPoint": [
                  {
                    "@type": "ContactPoint",
                    "url": "https://cprtrainingdirect.com/contact",
                    "contactType": "customer service",
                    "areaServed": "US-FL",
                    "availableLanguage": "English"
                  },
                  {
                    "@type": "ContactPoint",
                    "url": "https://cprtrainingdirect.com/contact",
                    "contactType": "customer service",
                    "areaServed": "US-GA",
                    "availableLanguage": "English"
                  }
                ]
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://cprtrainingdirect.com/#orlando",
                "name": "CPR Training Direct - Orlando",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Orlando",
                  "addressRegion": "FL",
                  "addressCountry": "US"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 28.5383,
                  "longitude": -81.3792
                },
                "url": "https://cprtrainingdirect.com/contact",
                "priceRange": "$55-$95",
                "openingHoursSpecification": [
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                    "opens": "08:00",
                    "closes": "18:00"
                  },
                  {
                    "@type": "OpeningHoursSpecification",
                    "dayOfWeek": "Saturday",
                    "opens": "09:00",
                    "closes": "15:00"
                  }
                ]
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://cprtrainingdirect.com/#atlanta",
                "name": "CPR Training Direct - Atlanta",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Atlanta",
                  "addressRegion": "GA",
                  "addressCountry": "US"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 33.749,
                  "longitude": -84.388
                },
                "url": "https://cprtrainingdirect.com/contact",
                "priceRange": "$55-$95"
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Slideshow - Auto-Advancing with 3 Slides */}
      <HeroSlideshow autoAdvanceInterval={10000} />

      {/* Regional Service Badges - Brutalist Cards with AOS */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6">
            <div
              data-aos="fade-up"
              data-aos-delay="0"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white border-4 border-indigo_dye-500 shadow-[4px_4px_0px_0px_rgba(2,5,18,1)] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(2,5,18,1)] transition-all duration-300"
            >
              <span className="text-3xl">📍</span>
              <span className="font-display font-bold uppercase tracking-wide text-gray-900">
                Orlando & Central Florida
              </span>
            </div>
            <div
              data-aos="fade-up"
              data-aos-delay="150"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white border-4 border-indigo_dye-500 shadow-[4px_4px_0px_0px_rgba(2,5,18,1)] hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(2,5,18,1)] transition-all duration-300"
            >
              <span className="text-3xl">📍</span>
              <span className="font-display font-bold uppercase tracking-wide text-gray-900">
                Atlanta & Surrounding Areas
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview - 3-Column Brutalist Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6">
          <h2
            data-aos="fade-up"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider text-center mb-16 text-indigo_dye-500"
          >
            What We Offer
          </h2>
          <ServicesOverview services={services} />
        </div>
      </section>

      {/* Accreditation Logos */}
      <AccreditationLogos logos={accreditationLogos} />

      {/* Testimonials Carousel */}
      <TestimonialsCarousel testimonials={featuredTestimonials} />

      {/* Bottom CTA Banner */}
      <CTABanner
        title="Ready to Get Certified?"
        subtitle="Book your CPR certification class today and receive your card the same day!"
        buttonText="Book Now"
        buttonLink="/book"
        backgroundColor="bg-red-600"
      />
    </>
  );
};

export default CPRHome;
